// ─── Types ────────────────────────────────────────────────────────────────────

type JsonLdObject = Record<string, unknown>
type JsonLdBlock = { jsonld: JsonLdObject }

export function hasJsonLdType(
  schemas: { jsonld: unknown }[] | null | undefined,
  expectedTypes: string | string[]
): boolean {
  const expected = Array.isArray(expectedTypes) ? expectedTypes : [expectedTypes]

  const hasType = (schema: unknown): boolean => {
    if (Array.isArray(schema)) return schema.some(hasType)
    if (!schema || typeof schema !== 'object') return false

    const schemaObject = schema as Record<string, unknown>
    const types = Array.isArray(schemaObject['@type'])
      ? schemaObject['@type']
      : [schemaObject['@type']]
    const matchesType = types.some(
      (type) =>
        typeof type === 'string' &&
        expected.some(
          (schemaType) =>
            type === schemaType ||
            type.endsWith(`/${schemaType}`) ||
            type.endsWith(`#${schemaType}`)
        )
    )

    return matchesType || hasType(schemaObject['@graph'])
  }

  return schemas?.some(({ jsonld }) => hasType(jsonld)) ?? false
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Contentful locale-wrapper detection.
 * Remove this regex + the unwrapping block in `readObject` if your CMS
 * does NOT wrap field values in `{ "en-US": ... }` locale keys.
 */
const LOCALE_KEY_RE = /^[a-z]{2}(-[A-Z]{2})?$/

function asList(x: unknown): Record<string, unknown>[] | null {
  if (x == null) return null
  if (Array.isArray(x)) return x.length ? x : null
  return typeof x === 'object' ? [x as Record<string, unknown>] : null
}

/**
 * Turn a CMS JSON field into a plain object.
 * Handles: locale wrappers, JSON strings, and direct objects.
 */
function readObject(val: unknown): Record<string, unknown> | null {
  if (val == null) return null

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let v: any = val

  // --- Locale unwrapping (Contentful-specific) ---
  // If your CMS does NOT use locale wrappers, delete this block.
  if (v && typeof v === 'object' && !Array.isArray(v) && !(v as Record<string, unknown>).sys) {
    const keys = Object.keys(v)
    if (keys.length > 0 && keys.length <= 6 && keys.every((k) => LOCALE_KEY_RE.test(k))) {
      const record = v as Record<string, unknown>
      v = record['en-US'] ?? record['en-us'] ?? Object.values(record)[0]
    }
  }

  // --- JSON string parsing ---
  if (typeof v === 'string') {
    try {
      v = JSON.parse(v)
    } catch {
      return null
    }
  }

  return v && typeof v === 'object' ? (v as Record<string, unknown>) : null
}

/**
 * Extract the jsonld object from a single schema block.
 * Tries multiple field naming conventions: jsonld, jsonLd, fields.jsonld, fields.jsonLd
 */
function ldFromBlock(
  block: Record<string, unknown> | null | undefined
): Record<string, unknown> | null {
  if (block == null) return null
  const f = block.fields as Record<string, unknown> | undefined
  return readObject((block.jsonld ?? block.jsonLd ?? f?.jsonld ?? f?.jsonLd) as unknown)
}

// ─── Normalization ───────────────────────────────────────────────────────────

/**
 * Normalize raw CMS schema items into a uniform `Array<{ jsonld: object }>`.
 *
 * Handles:
 * - Direct schema blocks with a `jsonld` field
 * - Schema groups that contain nested `jsonLdSchemas` arrays
 * - Locale wrappers and JSON strings
 *
 * @param {any[]} raw — array of schema entries from the CMS
 * @returns {{ jsonld: object }[] | null}
 */
export function normalizeJsonLdSchemaItems(raw: unknown[]): JsonLdBlock[] | null {
  if (!Array.isArray(raw) || !raw.length) return null

  const out = []

  for (const item of raw) {
    // Direct schema block
    const entry = item as Record<string, unknown>
    const direct = ldFromBlock(entry)
    if (direct) {
      out.push({ jsonld: direct })
      continue
    }

    // Nested schema group (a wrapper entry containing child schema blocks)
    const fields = entry?.fields as Record<string, unknown> | undefined
    const kids = asList(entry?.jsonLdSchemas ?? fields?.jsonLdSchemas)
    if (!kids) continue

    for (const child of kids) {
      const parsed = ldFromBlock(child)
      if (parsed) {
        out.push({ jsonld: parsed })
      }
    }
  }

  return out.length ? out : null
}

// ─── DOM ID generation ───────────────────────────────────────────────────────

function slug(s: unknown): string {
  if (typeof s !== 'string' || !s.trim()) return ''
  return s
    .trim()
    .replace(/^https?:\/\//i, '')
    .replace(/[^a-zA-Z0-9_-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 96)
}

function typeSlug(t: unknown): string {
  return slug(Array.isArray(t) ? t.filter(Boolean).join('-') : String(t))
}

/** Build a unique segment from JSON-LD content: prefers @id, then @graph[0], then @type */
function jsonLdDomIdSegment(jsonld: JsonLdObject): string {
  // Try @id
  let seg = typeof jsonld['@id'] === 'string' ? slug(jsonld['@id']) : ''
  if (seg) return seg

  // Try first @graph node
  const g = jsonld['@graph']
  if (Array.isArray(g) && g[0] && typeof g[0] === 'object') {
    const node = g[0]
    seg = typeof node['@id'] === 'string' ? slug(node['@id']) : ''
    if (seg) return seg
    if (node['@type']) {
      seg = typeSlug(node['@type'])
      if (seg) return seg
    }
  }

  // Fall back to @type
  if (jsonld['@type']) {
    seg = typeSlug(jsonld['@type'])
    if (seg) return seg
  }

  return ''
}

/**
 * Generate a stable, deterministic DOM id for a JSON-LD <script> tag.
 *
 * @param {object} jsonld — the JSON-LD object
 * @param {number} index  — positional index (for uniqueness when multiple schemas share the same @type)
 * @returns {string}
 */
export function getJsonLdDomId(jsonld: unknown, index: number): string {
  const n = Number.isFinite(index) ? index : 0
  const idFor = (seg: string) => (seg ? `jsonld-cms-${seg}-${n}` : `jsonld-cms-slot-${n}`)

  if (!jsonld || typeof jsonld !== 'object') return idFor('')
  return idFor(jsonLdDomIdSegment(jsonld as JsonLdObject))
}
